/**
 * Roles & permissions — one declarative source of truth shared by server (enforcement)
 * and web (show/hide UI). No permission matrix sprawl: one map, one `can()` function.
 */
export const ROLES = [
    'Admin',
    'Editor',
    'Sales',
    'Purchasing',
    'Viewer',
    'Warehouse',
    'Security',
    'Finance',
];
/** Every guarded action in the system. Add new actions here as pillars land. */
export const ACTIONS = [
    'read',
    'customer.write',
    'deal.write',
    'price.write',
    'delivery.write',
    'cheque.write',
    'doc.write',
    'purchase.write',
    'config.write',
    'user.manage',
    'audit.read',
    'ai.use',
    'cashflow.write',
    'motor.design',
    'release.prepare',
    'release.check',
    'release.approve',
    'release.security',
    'accounting.read',
    'accounting.post',
    'dashboard.read',
    'ownernet.read',
];
/**
 * Which roles may perform each action. Admin is granted everything explicitly for auditability.
 *
 * EDITOR is the owner's general "edit" tier (mirrors Viewer=read-only, Admin=full): the day-to-day business
 * operator — it may write deals, customers, prices, deliveries, cheques, docs, purchases, motor designs, and
 * READ the books/dashboard. It deliberately CANNOT: administer the system (config.write / user.manage /
 * audit.read), post to the GL (accounting.post), move cash (cashflow.write), or sit in the two-person signed-
 * release chain (release.*) — those stay segregated (SoD) so one Editor can never both act and approve.
 */
export const PERMISSIONS = {
    read: ['Admin', 'Editor', 'Sales', 'Purchasing', 'Viewer', 'Warehouse', 'Security', 'Finance'],
    'customer.write': ['Admin', 'Editor', 'Sales'],
    'deal.write': ['Admin', 'Editor', 'Sales'],
    'price.write': ['Admin', 'Editor', 'Purchasing'],
    'delivery.write': ['Admin', 'Editor', 'Sales'],
    'cheque.write': ['Admin', 'Editor', 'Sales', 'Purchasing'],
    'doc.write': ['Admin', 'Editor', 'Sales', 'Purchasing'],
    'purchase.write': ['Admin', 'Editor', 'Purchasing'],
    'config.write': ['Admin'],
    'user.manage': ['Admin'],
    'audit.read': ['Admin'],
    'ai.use': ['Admin'],
    // Setting the opening cash balance / logging cash facts — Admin + Finance.
    'cashflow.write': ['Admin', 'Finance'],
    // Saving a motor design run to the shared history. Engineering-facing; not for read-only Viewers.
    'motor.design': ['Admin', 'Editor', 'Sales', 'Purchasing'],
    // --- Two-person signed release (CIRCULARITY_360 Phase D). Segregation of duties: a single human can
    //     never fill two signature slots on one release — enforced at the route, not just here. Editor is
    //     intentionally OUT of this chain. ---
    'release.prepare': ['Admin', 'Sales', 'Warehouse'],
    'release.check': ['Admin', 'Sales', 'Finance'],
    'release.approve': ['Admin', 'Finance'],
    'release.security': ['Admin', 'Security'],
    // --- Accounting (LEAVE_SEPIDAR_PLAN.md Phase 1). The books are Finance-owned; Editor may READ, not post. ---
    'accounting.read': ['Admin', 'Editor', 'Finance'],
    'accounting.post': ['Admin', 'Finance'],
    // The owner-net control tower (owner-net, margin-at-risk, price-truth) — management BI. The pure
    // operational roles (Warehouse dispatch, Security gate) have no need for it; everyone else does (RBAC).
    'dashboard.read': ['Admin', 'Editor', 'Sales', 'Purchasing', 'Finance', 'Viewer'],
    // «سود خالص مالک» (ownerNet) is the OWNER's ownership number (NORTH_STAR §2 "for ownership, not
    // accounting") — strictly tighter than dashboard.read. The operational team keeps the dashboard (gross,
    // margin, working-capital carry) but NOT this bottom line. Gated at BOTH ends: the route nulls ownerNet
    // AND its gaMonthly input (else it is trivially recomputable) for callers without this; the web hides the
    // tile. Reversible by adding roles here (Q-8).
    'ownernet.read': ['Admin', 'Finance'],
};
/** Pure permission check — safe to use on both server and client. */
export function can(role, action) {
    return PERMISSIONS[action].includes(role);
}
export const ROLE_LABELS_FA = {
    Admin: 'مدیر',
    Editor: 'ویرایشگر',
    Sales: 'فروش',
    Purchasing: 'خرید',
    Viewer: 'بازبین',
    Warehouse: 'انبار',
    Security: 'حراست',
    Finance: 'مالی',
};
/** English twin of ROLE_LABELS_FA — for English screens (staged i18n rollout). */
export const ROLE_LABELS_EN = {
    Admin: 'Admin',
    Editor: 'Editor',
    Sales: 'Sales',
    Purchasing: 'Purchasing',
    Viewer: 'Viewer',
    Warehouse: 'Warehouse',
    Security: 'Security',
    Finance: 'Finance',
};
//# sourceMappingURL=roles.js.map